import React from 'react';
import { Link } from 'react-router-dom';
import ReactGA from "react-ga4";

const Intro = () => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "About" });
    return (
        <>
            <div className="m-auto max-w-6xl p-2 md:p-12 h-5/6" id='about'>
                <div className="flex flex-col-reverse lg:flex-row py-8 justify-between lg:text-left" data-aos="fade-up">
                    {/* Left — Visual */}
                    <div className="lg:w-1/2 flex flex-col lg:mx-4 justify-center items-center mt-8 lg:mt-0">
                        <div className="relative w-full max-w-sm">
                            {/* Phone mockup with chat bubbles */}
                            <div className="bg-gray-900 border border-gray-700 rounded-3xl p-4 shadow-2xl">
                                {/* Phone header */}
                                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-700">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                        </div>
                                        <div>
                                            <p className="text-white text-sm font-semibold">ScanWiz AI</p>
                                            <div className="flex items-center gap-1">
                                                <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                                                <p className="text-green-400 text-xs">Connected</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                                        <span className="text-blue-400 text-xs font-mono">BT</span>
                                    </div>
                                </div>
                                {/* Chat messages */}
                                <div className="space-y-3">
                                    <div className="flex justify-start">
                                        <div className="bg-gray-700 rounded-2xl rounded-tl-none px-4 py-2 max-w-xs">
                                            <p className="text-gray-100 text-sm">Why is my check engine light on?</p>
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <div className="bg-blue-700 rounded-2xl rounded-tr-none px-4 py-2 max-w-xs">
                                            <p className="text-blue-100 text-sm">Code P0420 detected — Catalyst Efficiency Below Threshold (Bank 1). This usually means the catalytic converter is aging. I recommend a professional inspection.</p>
                                        </div>
                                    </div>
                                    <div className="flex justify-start">
                                        <div className="bg-gray-700 rounded-2xl rounded-tl-none px-4 py-2 max-w-xs">
                                            <p className="text-gray-100 text-sm">Is it safe to drive?</p>
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <div className="bg-blue-700 rounded-2xl rounded-tr-none px-4 py-2 max-w-xs">
                                            <p className="text-blue-100 text-sm">Yes, generally safe for short trips. Avoid prolonged highway driving until resolved. ✓</p>
                                        </div>
                                    </div>
                                </div>
                                {/* Input bar */}
                                <div className="mt-4 bg-gray-800 rounded-xl px-3 py-2 flex items-center gap-2">
                                    <span className="text-gray-500 text-sm flex-1">Ask your AI mechanic...</span>
                                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right — Text */}
                    <div className="flex-col my-4 text-center lg:text-left lg:my-0 lg:justify-end w-full lg:w-1/2 px-8" data-aos="zoom-in" data-aos-delay="500">
                        <div className="inline-flex items-center mb-3">
                            <span className="text-blue-600 font-bold uppercase text-xs tracking-widest border-l-4 border-blue-600 pl-3">About the Product</span>
                        </div>
                        <h3 className="text-3xl text-blue-900 font-bold mb-4">Meet Your AI Mechanic in Your Pocket</h3>
                        <div>
                            <p className='my-3 text-lg text-gray-600 font-medium'>
                                <strong>AI ScanWiz Pro</strong> is a pocket-sized OBD2 Bluetooth adapter that pairs with your smartphone and puts the power of artificial intelligence between you and your car's data.
                            </p>
                        </div>
                        <div>
                            <p className='my-3 text-lg text-gray-600 font-medium'>
                                Simply plug the device into your car's OBD2 port, connect via Bluetooth 4.0, and start a conversation. Ask about fault codes, engine health, fuel efficiency, sensor readings — in plain English. No jargon. No guessing.
                            </p>
                        </div>
                        <div>
                            <p className='my-3 text-lg text-gray-600 font-medium'>
                                Compatible with all 12V vehicles supporting OBDII/EOBD protocols. Works on both iOS and Android.
                            </p>
                        </div>
                        <Link to="/get-demo" className="text-white bg-blue-900 hover:bg-blue-800 inline-flex items-center justify-center w-full px-6 py-3 my-4 text-lg shadow-xl rounded-2xl sm:w-auto sm:mb-0 group">
                            Get Yours Today
                            <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                        </Link>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Intro;
